import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  EmptySearchResult,
  FormLayout,
  Icon,
  LegacyStack,
  Link,
  Modal,
  Pagination,
  ResourceItem,
  ResourceList,
} from "@shopify/polaris";
import { PlusCircleIcon, MinusCircleIcon } from "@shopify/polaris-icons";
import HorseVariant from "./HorseVariant/HorseVariant";
import { HorseVariantSearchFieldAndMoreFilters } from "../horse_variants/index/support/HorseVariantSearchFieldAndMoreFilters";
import type { DecimalRangeFilter, HorseVariantsQueryParams, IHorseVariant } from "../../api_utils/types";
import { useHorseVariants } from "../../api_utils/requests";
import { extractMessageFromError } from "../../helper_functions/utils";
import ConfirmationDialog from "./ConfirmationDialog";
import type { QueryKey} from "@tanstack/react-query";
import { useQueryClient, type UseQueryResult } from "@tanstack/react-query";

const emptyState = <EmptySearchResult title="No variants found" withIllustration />;

const defaultInitialQuantity: DecimalRangeFilter = { more_than: undefined, less_than: undefined };
const emptyArray: number[] = [];

export default function AddVariantDialog({
  active,
  handleVariantUpdate,
  initialHorseLocationId,
  initialQuantity = defaultInitialQuantity,
  initialSupplierId,
  initialVendor,
  primary = false,
  removeAllVariants,
  querySelected,
  queryKey,
  selectionKey,
  setActive,
  setErrorMessage,
  updateSelection,
  excludableHorseVariantIds = emptyArray,
}: {
  readonly active: boolean;
  readonly handleVariantUpdate: (response: any) => Promise<void> | void;
  readonly initialHorseLocationId?: number;
  readonly initialQuantity?: DecimalRangeFilter;
  readonly initialSupplierId?: number;
  readonly initialVendor?: string;
  readonly primary?: boolean;
  readonly removeAllVariants?: () => Promise<void>;
  readonly querySelected: UseQueryResult<{horse_variant_id: number}[]>;
  readonly queryKey: QueryKey;
  readonly setActive: (active: boolean) => void;
  readonly setErrorMessage: (errorMessage: string) => void;
  readonly selectionKey:
    | "bundle_horse_variants_attributes"
    | "purchase_order_line_items_attributes"
    | "supplier_horse_variants_attributes"
    | "transfer_order_horse_variants_attributes";
  readonly updateSelection: (payload: any, filters: any) => Promise<{ horse_variant_ids: number[] }>;
  readonly excludableHorseVariantIds?: number[];
}): React.JSX.Element {
  const [filters, setFilters] = useState<HorseVariantsQueryParams>({});
  const handleSetFilters = useCallback((newFilters: HorseVariantsQueryParams) => {
    // Filters changed so not all-all selected
    setAllItemsSelected(false);
    setFilters(newFilters);
    setPageNum(1);
  }, []);

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set<number>());
  const [allItemsSelected, setAllItemsSelected] = useState(false); // all variants for all pages
  const [pageNum, setPageNum] = useState(1);

  const [isConfirmRemoveOpen, setIsConfirmRemoveOpen] = useState(false);
  const queryClient = useQueryClient();

  const refetchSelectedIds = useCallback(async () => {
    // Use the query from 
    if (queryKey) {
      await queryClient.cancelQueries({ queryKey: queryKey });
    }
    const { data: serverSelectedIds  } = await querySelected.refetch();
    setSelectedIds(new Set<number>(serverSelectedIds.map(({ horse_variant_id }) => horse_variant_id)));
  }, [querySelected, setSelectedIds, queryKey, queryClient]);

  const [isReady, setIsReady] = useState(false);
  const fetchInitialSelectedIds = useCallback(async () => {
    if (active) {
      await refetchSelectedIds();
      setIsReady(true);
    }
  }, [active, refetchSelectedIds, setIsReady]);

  useEffect(() => {
    void fetchInitialSelectedIds();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const {
    data: { rows, hasNext, hasPrev },
  } = useHorseVariants({ ...filters, page: pageNum });
  const horseVariants = rows.filter((horseVariant) => {
    return !excludableHorseVariantIds.includes(horseVariant.id);
  });
  const allSelectedOnCurrentPage = horseVariants.every((horseVariant) => selectedIds.has(Number(horseVariant.id)));

  const updateSelectionAPI = useCallback(
    async (payload, successMessage: string): Promise<void> => {
      try {
        const response = await updateSelection(payload, filters);
        void handleVariantUpdate(response);
      } catch (error: unknown) {
        Rollbar.error(error, payload);
        const newErrorMessage = extractMessageFromError(error);
        setErrorMessage(newErrorMessage);
        return;
      }
      shopify.toast.show(successMessage);

      void refetchSelectedIds();
    },
    [updateSelection, filters, handleVariantUpdate, setErrorMessage, refetchSelectedIds],
  );

  const callUpdateSelection = useCallback(
    async (newSelectedIds: Set<number>, successMessage: string): Promise<void> => {
      await updateSelectionAPI({
        [selectionKey]: Array.from(newSelectedIds).map((id) => ({ horse_variant_id: id })),
      }, successMessage);
    },
    [updateSelectionAPI, selectionKey],
  );

  const addAllOnCurrentPage = useCallback(async () => {
    const newSelectedIds = new Set<number>(selectedIds);
    horseVariants.forEach((horseVariant) => {
      newSelectedIds.add(Number(horseVariant.id));
    });
    setSelectedIds(newSelectedIds);

    try {
      await callUpdateSelection(newSelectedIds, `${newSelectedIds.size - selectedIds.size} variants added`);
    } catch (_error: unknown) {
      setSelectedIds(selectedIds);
    }
  }, [selectedIds, setSelectedIds, horseVariants, callUpdateSelection]);

  const [variantIdsToRemove, setVariantIdsToRemove] = useState<string[]>([]);

  const handleRemoveAllOnCurrentPage = useCallback(() => {
    const variants_to_remove = horseVariants.map(({ id }) => id.toString());
    setVariantIdsToRemove(variants_to_remove);
    setIsConfirmRemoveOpen(true);
  }, [setIsConfirmRemoveOpen, setVariantIdsToRemove, horseVariants]);

  const handleRemoveAllVariants = useCallback(() => {
    setVariantIdsToRemove([]);
    setIsConfirmRemoveOpen(true);
  }, [setIsConfirmRemoveOpen, setVariantIdsToRemove]);

  const removeAllCallback = useCallback(async () => {
    // We're removing, so all-all not selected
    const originalAllItemsSelected = allItemsSelected;
    const originalSelectedIds = new Set(selectedIds.values());
    setAllItemsSelected(false);
    setIsConfirmRemoveOpen(false);

    try {
      let newSelectedIds: Set<number>;
      if (variantIdsToRemove.length === 0) {
        newSelectedIds = new Set<number>();
        setSelectedIds(newSelectedIds);

        await removeAllVariants();
        shopify.toast.show("All variants removed");
      } else {
        newSelectedIds = new Set(selectedIds);
        variantIdsToRemove.forEach((id: string) => {
          newSelectedIds.delete(Number(id));
        });
        setSelectedIds(newSelectedIds);
        setVariantIdsToRemove([]);

        await callUpdateSelection(newSelectedIds, `${variantIdsToRemove.length} variants removed`);
      }
    } catch (_error: unknown) {
      setSelectedIds(originalSelectedIds);
      setAllItemsSelected(originalAllItemsSelected);
      setIsConfirmRemoveOpen(true);
    }
  }, [
    removeAllVariants,
    selectedIds,
    variantIdsToRemove,
    setIsConfirmRemoveOpen,
    setVariantIdsToRemove,
    callUpdateSelection,
    allItemsSelected,
  ]);

  const addAllVariants = useCallback(async () => {
    // Only after clicking the add-all button, we can be sure that all-all is not selected
    setAllItemsSelected(true);

    const originalSelectedIds = new Set(selectedIds.values());

    try {
      await updateSelectionAPI({ select_all: true }, `Matching variants added`);
    } catch (error: unknown) {
      setSelectedIds(originalSelectedIds);
    }
  }, [updateSelectionAPI, setAllItemsSelected, selectedIds]);

  const toggleSingleVariant = useCallback(
    async (variant: IHorseVariant) => {
      const newSelectedIds = new Set(selectedIds);
      let toastSuccessMessage: string;
      if (selectedIds.has(variant.id)) {
        newSelectedIds.delete(variant.id);
        setSelectedIds(newSelectedIds);
        setAllItemsSelected(false);
        toastSuccessMessage = "Variant removed";
      } else {
        newSelectedIds.add(variant.id);
        toastSuccessMessage = "Variant added";
        setSelectedIds(newSelectedIds);
      }

      try {
        await callUpdateSelection(newSelectedIds, toastSuccessMessage);
      } catch (error: unknown) {
        setSelectedIds(selectedIds);
      }
    },
    [selectedIds, setSelectedIds, setAllItemsSelected, callUpdateSelection],
  );

  const addItem = useCallback(
    async (id: string) => {
      const idNumber = Number(id);
      const horseVariant = horseVariants.find((variant) => variant.id === idNumber);
      await toggleSingleVariant(horseVariant);
    },
    [toggleSingleVariant, horseVariants],
  );

  const renderItem = useCallback(
    (horseVariant: IHorseVariant) => {
      return (
        <ResourceItem
          accessibilityLabel={`View details for ${horseVariant.id}`}
          id={horseVariant.id.toString()}
          media={
            <div style={{ transform: "translateY(170%)" }}>
              {selectedIds.has(horseVariant.id) ? <Icon source={MinusCircleIcon} /> : <Icon source={PlusCircleIcon} />}
            </div>
          }
          onClick={addItem}
        >
          <HorseVariant disableClick showAvailable variant={horseVariant} />
        </ResourceItem>
      );
    },
    [selectedIds, addItem],
  );

  const setPageNumAndScroll = useCallback((newPageNum: number) => {
    setPageNum(newPageNum);
    document.getElementById("variant-header")?.scrollIntoView();
  }, []);
  const nextPage = useCallback(() => {
    if (hasNext) {
      setPageNumAndScroll(pageNum + 1);
    }
  }, [hasNext, pageNum, setPageNumAndScroll]);
  const prevPage = useCallback(() => {
    if (hasPrev) {
      setPageNumAndScroll(pageNum - 1);
    }
  }, [hasPrev, pageNum, setPageNumAndScroll]);

  const handleOpen = useCallback(() => {
    setActive(true);
  }, [setActive]);
  const handleClose = useCallback(() => {
    setActive(false);
  }, [setActive]);
  const handleCloseConfirmation = useCallback(() => {
    setIsConfirmRemoveOpen(false);
  }, []);

  return (
    <Modal
      activator={
        <Button onClick={handleOpen} variant={primary ? "primary" : "secondary"}>
          Add variants
        </Button>
      }
      limitHeight
      onClose={handleClose}
      open={active}
      primaryAction={{
        content: "Close",
        onAction: handleClose,
      }}
      title="Add variants"
    >
      <ConfirmationDialog
        active={isConfirmRemoveOpen}
        content={
          variantIdsToRemove.length > 0
            ? `Are you sure that you want to remove ${variantIdsToRemove.length} variants?`
            : "Are you sure that you want to remove all variants?"
        }
        isDestructive
        okText="Remove"
        onCancel={handleCloseConfirmation}
        onClose={handleCloseConfirmation}
        onOk={removeAllCallback}
        title="Confirm bulk removal"
      />
      <Modal.Section>
        <FormLayout>
          <FormLayout.Group>
            <HorseVariantSearchFieldAndMoreFilters
              disableTabs
              initialHorseLocationId={initialHorseLocationId}
              initialQuantity={initialQuantity}
              initialSupplierId={initialSupplierId}
              initialVendor={initialVendor}
              setFilters={handleSetFilters}
            />
          </FormLayout.Group>
          <FormLayout.Group>
            <LegacyStack>
              <LegacyStack.Item>
                <div id="variant-header" style={{ marginLeft: "12px", cursor: "pointer" }}>
                  {allSelectedOnCurrentPage || allItemsSelected ? (
                    <div onClick={handleRemoveAllOnCurrentPage}>
                      <Icon source={MinusCircleIcon} />
                    </div>
                  ) : (
                    <div onClick={addAllOnCurrentPage}>
                      <Icon source={PlusCircleIcon} />
                    </div>
                  )}
                </div>
              </LegacyStack.Item>
              <LegacyStack.Item>
                <div style={{ marginBottom: "15px" }}>
                  {allSelectedOnCurrentPage || allItemsSelected ? (
                    <Link onClick={allItemsSelected ? handleRemoveAllVariants : addAllVariants} url="#">
                      {allItemsSelected ? "Remove all variants" : "Add all variants that match this search"}
                    </Link>
                  ) : (
                    `${selectedIds.size} added`
                  )}
                </div>
              </LegacyStack.Item>
            </LegacyStack>
          </FormLayout.Group>
        </FormLayout>
        <ResourceList
          emptyState={emptyState}
          items={isReady ? horseVariants : []}
          loading={!isReady}
          renderItem={renderItem}
          resourceName={{
            singular: "variant",
            plural: "variants",
          }}
        />
      </Modal.Section>
      <Modal.Section>
        <LegacyStack alignment="center" distribution="center">
          <Pagination
            hasNext={hasNext}
            hasPrevious={hasPrev}
            nextKeys={[39]}
            onNext={nextPage}
            onPrevious={prevPage}
            previousKeys={[37]}
          />
        </LegacyStack>
      </Modal.Section>
    </Modal>
  );
}
